import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import "./AffiliateDashboard.scss";
import { useTranslation } from "react-i18next";
import { Typography, CircularProgress, Button } from "@mui/material";
import AffiliateService from "../../Services/http/AffiliateService";
import { AffiliateAccountItem, AffiliateDashboardSummary } from "../../types/AffiliateDashboardTypes";
import DataGridTamplet from "../Helper/TableTemplet/DataGridTamplet";
import { useNavigate } from "react-router-dom";

const AffiliateDashboard = () => {
  const { t } = useTranslation("AffiliateDashboard");
  const navigate = useNavigate();

  const [summary, setSummary] = useState<AffiliateDashboardSummary>({
    total_reimbursed: 0,
    total_commission: 0,
    total_better_refund_commission: 0,
  });

  const [accounts, setAccounts] = useState<AffiliateAccountItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchAffiliateDashboard();
  }, []);

  const fetchAffiliateDashboard = async () => {
    setIsLoading(true);
    try {
      const response = await AffiliateService.getAffiliateDashboard();
      setSummary(response.summary);
      setAccounts(response.accounts);
    } catch (error) {
      console.error("Failed to fetch affiliate dashboard", error);
    } finally {
      setIsLoading(false);
    }
  };

  const columns = [
    { field: "account_name", headerName: t("accountName"), flex: 1, headerAlign: "center", align: "center" },
    {
      field: "joined_at",
      headerName: t("createdAt"),
      flex: 1,
      headerAlign: "center",
      align: "center",
      valueFormatter: (params) =>
        params.value ? new Date(params.value).toLocaleDateString() : "",
    },
    {
      field: "reimbursed_total",
      headerName: t("reimbursedAmount"),
      flex: 1,
      headerAlign: "center",
      align: "center",
      type: "number",
      valueFormatter: ({ value }) =>
        value != null
          ? `$${value.toLocaleString(undefined, { minimumFractionDigits: 2 })}`
          : "$0.00",
    },
    {
      field: "commission_percentage",
      headerName: t("betterRefundFeePercentage"),
      flex: 1,
      headerAlign: "center",
      align: "center",
      type: "number",
      valueFormatter: ({ value }) =>
        value != null ? `${value.toFixed(2)}%` : "0.00%",
    },
    {
      field: "better_refund_commission",
      headerName: t("betterRefundCommission"),
      flex: 1,
      headerAlign: "center",
      align: "center",
      type: "number",
      valueFormatter: ({ value }) =>
        value != null
          ? `$${value.toLocaleString(undefined, { minimumFractionDigits: 2 })}`
          : "$0.00",
    },
    {
      field: "affiliate_commission",
      headerName: t("affiliateCommission"),
      flex: 1,
      headerAlign: "center",
      align: "center",
      type: "number",
      valueFormatter: ({ value }) =>
        value != null
          ? `$${value.toLocaleString(undefined, { minimumFractionDigits: 2 })}`
          : "$0.00",
    },
  ];

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className="affiliate-dashboard-container">
      <h2>{t("title")}</h2>

      <Box className="cases-box">
        {[
          { label: t("reimbursedAmount"), value: summary.total_reimbursed, color: "#27AE60" },
          { label: t("betterRefundCommission"), value: summary.total_better_refund_commission, color: "#0037AB" },
          {
            label: t("affiliateCommission"),
            value: summary.total_commission,
            color: "#EB5757",
          },
        ].map((item, idx) => (
          <Box className="case-item" key={idx}>
            <Box className="case-item-two">
              <Box className="cases-sum-box" sx={{ color: item.color }}>
                {item.value != null
                  ? `$${item.value.toLocaleString(undefined, { minimumFractionDigits: 2 })}`
                  : "$0.00"}
              </Box>
              <Box className="cases-description-box">
                {item.label}
              </Box>
            </Box>
          </Box>
        ))}
      </Box>

      <Box className="grid-item">
        <Box className="grid-header">
          <Typography variant="h5" className="grid-title">
            {t("accountsTableTitle")}
          </Typography>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => navigate("/affiliate-layout/affiliate-monthly-breakdown")}
          >
            {t("viewMonthlyBreakdown")}
          </Button>
        </Box>

        <DataGridTamplet
          columns={columns}
          rows={accounts.map((acc, index) => ({
            id: index,
            ...acc,
          }))}
          filterInClientSide={false}
          height="350px"
        />
      </Box>
    </Box>
  );
};

export default AffiliateDashboard;
